"use client";

import { useCallback } from "react";
import type { BackendTextareaWithLabelProps } from "../../backend-textarea";
import { BackendTextareaWithLabel } from "../../backend-textarea";
import {
  useController,
  useFormContext,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

// Spezielle Textarea Input Komponente für mehrzeilige Text-Eingaben
export function ControlledTextareaInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onChange,
  onBlur,
  onFieldBlur,
  ...props
}: Omit<
  BackendTextareaWithLabelProps,
  "value" | "onChange" | "onBlur" | "error" | "name"
> & {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFieldBlur?: (fieldName: string) => void;
}) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control || formContext?.control;

  if (!actualControl) {
    throw new Error(
      "Controlled textarea must be used inside FormProvider or with control prop"
    );
  }

  const {
    field,
    fieldState: { error },
  } = useController({ name, control: actualControl });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const raw = e.target.value;
      field.onChange(raw);
      onChange?.(e);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      field.onBlur();
      onBlur?.(e);
      onFieldBlur?.(name);
    },
    [field, onBlur, onFieldBlur, name]
  );

  return (
    <BackendTextareaWithLabel
      {...props}
      value={field.value ?? ""}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error?.message}
      readOnly={props.readOnly}
    />
  );
}
